/**
 * カラム操作カスタムフック
 *
 * CSV列の追加・更新・削除・移動を管理するロジック
 */

import { useState, useCallback } from "react";
import { CsvColumn } from "../services/csvSettingsService";

interface UseColumnOperationsProps {
  initialColumns?: CsvColumn[];
}

interface UseColumnOperationsReturn {
  columns: CsvColumn[];
  setColumns: (columns: CsvColumn[]) => void;
  addColumn: () => void;
  updateColumn: (index: number, updates: Partial<CsvColumn>) => void;
  removeColumn: (index: number) => void;
  moveColumn: (index: number, direction: "up" | "down") => void;
  resetColumns: (newColumns: CsvColumn[]) => void;
}

const DEFAULT_COLUMNS: CsvColumn[] = [
  { order: 1, headerName: "氏名", jsonPath: "氏名", required: true },
  { order: 2, headerName: "金額", jsonPath: "金額", required: false },
];

/**
 * カラム操作フック
 */
export function useColumnOperations({
  initialColumns,
}: UseColumnOperationsProps = {}): UseColumnOperationsReturn {
  const [columns, setColumns] = useState<CsvColumn[]>(
    initialColumns || DEFAULT_COLUMNS
  );

  /**
   * 列を追加
   */
  const addColumn = useCallback(() => {
    setColumns((prev) => [
      ...prev,
      {
        order: prev.length + 1,
        headerName: "",
        jsonPath: "",
        required: false,
      },
    ]);
  }, []);

  /**
   * 列を更新
   * ヘッダー名が変更された場合、JSONパスも同じ値にする
   */
  const updateColumn = useCallback((index: number, updates: Partial<CsvColumn>) => {
    setColumns((prev) => {
      const newColumns = [...prev];
      if (updates.headerName !== undefined) {
        newColumns[index] = {
          ...newColumns[index],
          ...updates,
          jsonPath: updates.headerName,
        };
      } else {
        newColumns[index] = { ...newColumns[index], ...updates };
      }
      return newColumns;
    });
  }, []);

  /**
   * 列を削除
   */
  const removeColumn = useCallback((index: number) => {
    setColumns((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * 列を移動（上下）
   */
  const moveColumn = useCallback((index: number, direction: "up" | "down") => {
    setColumns((prev) => {
      const newColumns = [...prev];
      if (direction === "up" && index > 0) {
        [newColumns[index - 1], newColumns[index]] = [newColumns[index], newColumns[index - 1]];
      } else if (direction === "down" && index < newColumns.length - 1) {
        [newColumns[index], newColumns[index + 1]] = [newColumns[index + 1], newColumns[index]];
      }
      return newColumns;
    });
  }, []);

  /**
   * 列をリセット（外部からの再設定用）
   */
  const resetColumns = useCallback((newColumns: CsvColumn[]) => {
    setColumns(JSON.parse(JSON.stringify(newColumns)) as CsvColumn[]);
  }, []);

  return {
    columns,
    setColumns,
    addColumn,
    updateColumn,
    removeColumn,
    moveColumn,
    resetColumns,
  };
}
